/**
 * Public read path for a shared report.
 *
 * The opaque token in the link is the whole credential, so nothing here
 * attaches a session header — and the endpoint answers with the report body
 * and signed snapshot URLs only. A recipient never reaches the archive.
 */
import i18n from '@ohif/i18n';

/** A slice the report mentions, as a recipient sees it.
 *
 *  `snapshotUrl` carries the doctor's drawings; `cleanSnapshotUrl` is the same
 *  frame without them and is absent when nothing was drawn, and on reports
 *  written before both frames were stored. */
export type SharedSliceRef = {
  refId: string;
  sliceIndex: number;
  label?: string;
  snapshotUrl?: string | null;
  cleanSnapshotUrl?: string | null;
};

export type SharedReport = {
  reportId: string;
  title?: string;
  content?: Record<string, any> | null;
  sliceRefs?: SharedSliceRef[];
  signed?: boolean;
  signedAt?: string | null;
  signedBy?: { name?: string } | null;
  createdAt?: string;
  updatedAt?: string;
};

export type SharedReportPayload = {
  data: SharedReport;
  share: {
    audience: string;
    canDownload: boolean;
    expiresAt?: string | null;
  };
};

export function apiRoot(): string {
  // @ts-ignore - custom field on window.config
  return ((window.config?.medicaprofApiRoot as string) || '').replace(/\/$/, '');
}

export async function fetchSharedReport(shareToken: string): Promise<SharedReportPayload> {
  const res = await fetch(`${apiRoot()}/lab/shared/${encodeURIComponent(shareToken)}`);
  let payload: any = null;
  try {
    payload = await res.json();
  } catch (e) {
    /* empty body */
  }
  if (!res.ok || payload?.ok === 0) {
    throw new Error(payload?.detail || i18n.t('Medicaprof:Error.InvalidLink'));
  }
  return payload as SharedReportPayload;
}

/** The mentioned slices that actually have a picture to open. */
export function viewableSlices(report: SharedReport | null): SharedSliceRef[] {
  return (report?.sliceRefs || []).filter(ref => !!ref.snapshotUrl);
}
